import { gql } from 'apollo-boost'
import type ApolloClient from 'apollo-client'
import * as Sentry from '@sentry/react'
import { contentHash } from '@paintscout/util/builder/quote/util/content-hash'

export const TRANSLATE_WORK_ORDER_EMAIL_CONTENT = gql`
  query TranslateWorkOrderEmailContent($subject: String!, $body: String!, $lang: String!) {
    translateWorkOrderEmailContent(subject: $subject, body: $body, lang: $lang) {
      subject
      body
    }
  }
`

// Module-level (not per-component) so both SendQuoteDialog's template switch and SendQuoteForm's
// language switch share one cache -- toggling back and forth between templates/languages within
// the same Send dialog session (or re-opening it for the same quote) is then free instead of
// re-spending an Anthropic call on text already translated. Translation of fixed input text is
// deterministic, so a plain session-lifetime cache is safe; no invalidation is needed.
const MAX_CACHE_ENTRIES = 100
const translationCache = new Map<string, { subject: string; body: string }>()

function cacheKey(subject: string, body: string, lang: string): string {
  return `${lang}:${contentHash(`${subject} ${body}`)}`
}

function setCached(key: string, value: { subject: string; body: string }) {
  if (translationCache.size >= MAX_CACHE_ENTRIES) {
    const oldestKey = translationCache.keys().next().value
    translationCache.delete(oldestKey)
  }
  translationCache.set(key, value)
}

/**
 * Live-translates a work-order email's subject/body via the server's AI translation pipeline,
 * so whichever template is currently selected gets translated -- not just the one hardcoded
 * default template. Falls back to the original English content on English/no lang, and on any
 * request error (the send flow must never be blocked by a translation hiccup).
 */
export async function translateWorkOrderEmailContent({
  apolloClient,
  subject,
  body,
  lang
}: {
  apolloClient: ApolloClient<any>
  subject: string
  body: string
  lang?: string
}): Promise<{ subject: string; body: string }> {
  if (!lang || lang === 'en') {
    return { subject, body }
  }

  const key = cacheKey(subject, body, lang)
  const cached = translationCache.get(key)
  if (cached) {
    return cached
  }

  try {
    const { data } = await apolloClient.query<{
      translateWorkOrderEmailContent?: { subject: string; body: string }
    }>({
      query: TRANSLATE_WORK_ORDER_EMAIL_CONTENT,
      variables: { subject, body, lang },
      fetchPolicy: 'no-cache'
    })
    const result = data?.translateWorkOrderEmailContent ?? { subject, body }
    setCached(key, result)
    return result
  } catch (error) {
    Sentry.captureException(error, { tags: { lang, source: 'translateWorkOrderEmailContent' } })
    return { subject, body }
  }
}

/**
 * Same as translateWorkOrderEmailContent, but toggles a loading flag around the request so
 * callers can show a loading state without duplicating try/finally boilerplate at each call site.
 */
export async function translateWorkOrderEmailContentWithLoadingState({
  apolloClient,
  subject,
  body,
  lang,
  setLoading
}: {
  apolloClient: ApolloClient<any>
  subject: string
  body: string
  lang?: string
  setLoading: (loading: boolean) => void
}): Promise<{ subject: string; body: string }> {
  setLoading(true)
  try {
    return await translateWorkOrderEmailContent({ apolloClient, subject, body, lang })
  } finally {
    setLoading(false)
  }
}
